import type { ComponentContentSchema, PageDataMap } from '@vibesites/runtime';
import { mergePages } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
// Full inherited page list (base → company → last bake) plus this site's
// pages.json overlays, so `visible: false` (FAQ, virtual-tour, …) wins even
// when the baked merge is stale.
import bakedPages from '../vibesites-pages.json';
import localPages from '../pages.json';

interface SitemapItem {
  label: string;
  href: string;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface Review {
  rating?: number | string | null;
  review_text?: string | null;
}

/**
 * Upstream feeds sometimes pass literal strings like "false", "null", "none"
 * instead of an empty value — treat those as missing.
 */
function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (
    lower === 'false' ||
    lower === 'true' ||
    lower === 'null' ||
    lower === 'undefined' ||
    lower === 'none' ||
    lower === 'n/a'
  ) {
    return false;
  }
  return true;
}

const SITEMAP_ITEMS: SitemapItem[] = mergePages(
  bakedPages as PageDataMap,
  localPages as PageDataMap,
)
  .filter((page) => {
    if (page.alias === 'home' || page.path === '/') return false;
    if (page.visible === false) return false;
    if (page.template) return false;
    if (page.path === '/sitemap') return false;
    return true;
  })
  .map((page) => ({
    label: page.name,
    href: page.path,
  }));

export function SitemapContent() {
  // Hide /virtual-tour unless the property has at least one tour with a
  // usable URL (matches Header / VirtualTourEmbed), and hide /reviews
  // unless the property actually has reviews.
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const reviews = useCollectionBinding<Review>('sitemapcontent.reviews', {
    collection: 'reviews',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => isUsableUrl(t.virtual_tour_url));
  const hasReviews = (reviews || []).length > 0;

  const pages = SITEMAP_ITEMS.filter((item) => {
    if (item.href === '/virtual-tour' && !hasVirtualTours) return false;
    if (item.href === '/reviews' && !hasReviews) return false;
    return true;
  });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.href}
              href={page.href}
              className="block border border-black rounded px-6 py-4 text-center font-body text-sm text-black hover:bg-black hover:text-white transition-colors"
            >
              {page.label}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours (visibility check)',
      collection: 'virtual_tours',
    },
    'sitemapcontent.reviews': {
      label: 'Reviews (visibility check)',
      collection: 'reviews',
    },
  },
};

(SitemapContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SitemapContent;
